import css from '@styled-system/css';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import rem from '../../utils/rem';
import { Icon } from '../icon';
import { Box, Flex, Text } from '../primitives';
import RetryButton from './retry-button';

type Props = {
  fromMe: boolean;
  hour: string;
  id: string;
  onError?: (id: string, message: string) => void;
  text: string;
  readByMe: boolean;
  readByOther: boolean;
  unsent: boolean;
  avatar?: React.ReactNode;
  isLast: boolean;
};

const MOBILE_BORDER_RADIUS = `10px`;

const ConversationBubble = ({
  fromMe,
  hour,
  id,
  avatar,
  onError,
  text,
  readByMe,
  readByOther,
  unsent,
  isLast,
}: Props) => {
  const canRetry = fromMe && unsent;

  const retryCallback = useCallback(() => onError && onError(id, text), [
    id,
    text,
    onError,
  ]);

  //console.log(isLast, text);

  const bubbleContent = useMemo(
    () => (
      <>
        <Text
          wordWrap="break-word"
          color={
            [fromMe ? 'light' : 'primary', fromMe ? 'light' : 'primary'] as any
          }
          fontSize="size14"
          lineHeight={['inherit', rem(19)]}
          px="medium"
          pt="small"
          pb={['2px', '0px']}
        >
          {text}
        </Text>
        <Flex
          justifyContent="flex-end"
          pr={['4px', 'medium']}
          pl={['0px', 'medium']}
          pb={['2px', 'small']}
          alignItems="top"
        >
          <Text
            color={
              [
                fromMe ? 'light' : 'primary',
                fromMe ? 'light' : 'primary',
              ] as any
            }
            fontSize={['size12', rem(10)]}
            lineHeight={['inherit', rem(14)]}
            opacity={[0.75, 1]}
          >
            {hour}
          </Text>
          {isLast && !unsent && (
            <Flex pl="small" mr={rem(-7)} top={rem(2)}>
              {readByMe && <Icon name="check" color="light" size={9} />}
              {readByOther && (
                <Icon name="check" color="light" right={rem(7)} size={9} />
              )}
            </Flex>
          )}
        </Flex>
      </>
    ),
    [fromMe, hour, text]
  );

  const bubbleWithRetry = useMemo(
    () => (
      <>
        <RetryButton callback={retryCallback} />
        <SBubble
          fromMe
          as="button"
          border={0}
          borderTopLeftRadius={[MOBILE_BORDER_RADIUS, 'small']}
          borderTopRightRadius={0}
          onClick={retryCallback}
          textAlign="inherit"
          outline="none"
        >
          {bubbleContent}
        </SBubble>
      </>
    ),
    [bubbleContent, retryCallback]
  );

  const bubbleNormal = useMemo(
    () => (
      <SBubble
        fromMe={fromMe}
        borderTopLeftRadius={[
          fromMe ? MOBILE_BORDER_RADIUS : 0,
          fromMe ? 'small' : 0,
        ]}
        borderTopRightRadius={[
          fromMe ? 0 : MOBILE_BORDER_RADIUS,
          fromMe ? 0 : 'small',
        ]}
      >
        {bubbleContent}
      </SBubble>
    ),
    [fromMe, bubbleContent]
  );

  return (
    <Flex
      flexDirection={fromMe ? 'row-reverse' : 'row'}
      alignItems="center"
      mb={['xSmall', 'medium']}
    >
      {!fromMe && avatar && <Box mr={['small', 'small']}>{avatar}</Box>}
      {canRetry ? bubbleWithRetry : bubbleNormal}
    </Flex>
  );
};

const SBubble = styled(Box)<{ fromMe: boolean }>`
  ${({ fromMe }) =>
    css({
      bg: [fromMe ? 'primary2' : 'gallery', fromMe ? 'accent' : 'alabaster'],
      borderBottomLeftRadius: [MOBILE_BORDER_RADIUS, 'small'],
      borderBottomRightRadius: [MOBILE_BORDER_RADIUS, 'small'],
      maxWidth: '70%',
      minWidth: ['25%', 'auto'],
    }) as any}
`;

export default ConversationBubble;
